import React, {
    useState,
    useMemo,
    useEffect,
    forwardRef,
    useCallback,
    useRef,
    useImperativeHandle,
} from 'react';
import {
    Autocomplete,
    TextField,
    CircularProgress,
    debounce,
} from '@mui/material';
import {
    ICrewType,
    IFilter,
    IPagination,
    ISearch,
    DomainService,
} from '../services/DomainService';
import { IOrderItem } from '../model/Order';

interface Item {
    itemName: string;
}

interface CrewTypePickerProps {
    item: Item;
    selectedItems: IOrderItem[];
    index: number;
    width?: number;
    handleItemNameChange: (newValue: string, index: number) => void;
}

const CrewTypePicker = forwardRef<HTMLInputElement, CrewTypePickerProps>(
    ({ item, selectedItems, index, width, handleItemNameChange }, ref) => {
        const [allItems, setAllItems] = useState<ICrewType[]>([]);
        const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
        const [loading, setLoading] = useState(false);
        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
        useEffect(() => {
            if (ref && typeof ref === 'object' && ref.current) {
                ref.current.focus();
            }
        }, [ref]);
        // Initial fetch (once)
        const fetchInitialOptions = useCallback(async () => {
            try {
                setLoading(true);
                const objIPagination: IPagination = { perPage: 1000, page: 1 };
                const objIFilter: IFilter = {
                    columnName: 'activeDate',
                    columnValue: new Date().toISOString().split('T')[0],
                };
                const objISearch: ISearch = {
                    search: '',
                    sortBy: '',
                    sortOrder: '',
                    filterBy: objIFilter,
                    pagination: objIPagination,
                };

                const response = await DomainService.getPersonelCrewTypes(objISearch);

                if (!response || !Array.isArray(response.data)) {
                    throw new Error('Invalid response format.');
                }

                setAllItems(response.data);
            } catch (error) {
                console.error('Error fetching catalog data:', error);
                setAllItems([]);
            } finally {
                setLoading(false);
            }
        }, []);

        // Filter logic
        const filterOptions = useCallback(
            (search: string) => {
                const excludedNames = selectedItems
                    .filter(
                        (s) =>
                            (s.isActive === undefined && s.id === undefined) ||
                            s.isActive !== undefined
                    )
                    .map((s) => s.itemName);

                const filtered = allItems
                    .filter(
                        (item) =>
                            (!search ||
                                item.description?.toLowerCase().includes(search.toLowerCase())) &&
                            !excludedNames.includes(item.description ?? 'Unknown')
                    )
                    .map((item) => ({
                        label: item.description ?? 'Unknown',
                        value: String(item.crewTypeId),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setOptions(filtered);
            },
            [allItems, selectedItems]
        );

        const debouncedFilter = useMemo(() => debounce(filterOptions, 300), [filterOptions]);

        // Fetch once on mount
        useEffect(() => {
            fetchInitialOptions();
        }, [fetchInitialOptions]);

        // Filter when item name or selectedItems change
        useEffect(() => {
            debouncedFilter(item.itemName || '');
        }, [item.itemName, selectedItems, debouncedFilter]);

        // Ensure current item is in options if not already
        const [hasInjected, setHasInjected] = useState(false);
        useEffect(() => {
            if (item.itemName && !hasInjected) {
                setOptions((prev) => {
                    const exists = prev.some((opt) => opt.label === item.itemName);
                    return exists
                        ? prev
                        : [...prev, { label: item.itemName, value: '' }];
                });
                setHasInjected(true);
            }
        }, [item.itemName, hasInjected]);

        const handleInputChange = (event: React.SyntheticEvent, value: string) => {
            handleItemNameChange(value, index);
        };

        const handleChange = (event: React.SyntheticEvent, newValue: any) => {
            const newLabel = typeof newValue === 'string' ? newValue : newValue?.label || '';
            handleItemNameChange(newLabel, index);
        };

        return (
            <Autocomplete
                fullWidth={width ? false : true}
                style={{ width: width }}
                freeSolo
                options={options}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.label || ''
                }
                loading={loading}
                value={item.itemName || ''}
                onInputChange={handleInputChange}
                onChange={handleChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Please enter the item name (max 250 characters)"
                        variant="outlined"
                        inputRef={inputRef}
                        sx={{ height: 42.97 }}
                        InputProps={{
                            ...params.InputProps,
                            sx: { height: 42.97 },
                            endAdornment: (
                                <>
                                    {loading ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        );
    }
);

export default CrewTypePicker;